import { AlertModal, ErrorModal } from '@modusign/design-system';
import { useGetWorkspace } from '@modusign/openapi-client-user';
import { ReactNode } from 'react';
import { UseQueryResult } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useGroupListApi, useWorkspaceListApi } from '@app/hooks/workspace';
import workspaceService from '@app/libs/workspaceService';
import findActiveWorkspace from '@app/libs/workspaceService/findActiveWorkspace';
import { isAuthenticated } from '@app/store/account/selectors';
import { signOut } from '@app/store/account/sign-out/actions';
import { getName } from '@app/store/user/user/selectors';

interface Props {
  children: ReactNode;
  redirectToOnlyPathname?: boolean;
}

export default function Authentication({ children, redirectToOnlyPathname = true }: Props) {
  const dispatch = useDispatch();
  const { pathname, search, hash } = useLocation();
  const isAuth = useSelector(isAuthenticated);

  /**
   * @description
   * 워크스페이스 관련 정보를 앱 로딩시 불러오고,
   * 로딩, 에러일 때 DOM Tree를 suspend시켜
   * 하위 컴포넌트에서 마운트된 쿼리에 workspace data가 있음을 보장합니다.
   */
  const workspaceListQuery = useWorkspaceListApi();
  const groupListQuery = useGroupListApi();

  const userName = useSelector(getName);
  const workspaceId = useWorkspaceId();
  const workspaceQuery = useGetWorkspace(workspaceId);

  const queries: UseQueryResult<unknown, Error>[] = [workspaceListQuery, groupListQuery];

  if (!isAuth) {
    return (
      <Redirect
        to={`/authentication/signin?redirectTo=${encodeURIComponent(
          redirectToOnlyPathname ? pathname : `${pathname}${search}${hash}`
        )}`}
      />
    );
  }

  if (queries.some((l) => l.isError)) {
    return (
      <ErrorModal
        error={queries.find((l) => l.error)?.error || null}
        isOpen
        title="워크스페이스 불러오기 실패"
        onClose={() => {
          queries.forEach((l) => {
            if (l.isError) {
              l.refetch();
            }
          });

          if (queries.some((q) => q.failureCount > 3)) {
            dispatch(signOut());
          }
        }}
      />
    );
  }

  /**
   * FIXME: loadAccountDataSaga에서 setWorkspaceId를 call하고 있으므로,
   * 이 컴포넌트를 통해 workspace data를 보장하더라도 일시적으로 workspaceId가 없는 상태가 될 수 있습니다.
   * 이런 경우 마운트된 쿼리가 의도와 다르게 동작하므로 조건문에 추가합니다.
   */
  if (queries.some((l) => l.isLoading) || !workspaceId) {
    return <GlobalLoadingSpinner />;
  }

  if (
    workspaceQuery.isRefetchError &&
    workspaceQuery.error.message.toLowerCase() === 'forbidden workspace'
  ) {
    return (
      <AlertModal
        isOpen
        title="접근 안내"
        variant="warning"
        onClose={() => {
          const newActiveWorkspaceId = findActiveWorkspace(
            workspaceListQuery.data?.workspaces ?? []
          )?.id;
          if (newActiveWorkspaceId) {
            workspaceService.setWorkspaceId(newActiveWorkspaceId);
          } else {
            workspaceService.removeWorkspaceId();
          }
        }}
      >
        공용 워크스페이스에서 삭제되어 더 이상 접근할 수 없습니다. {userName}님의 개인
        워크스페이스로 이동하였습니다.
      </AlertModal>
    );
  }

  return children;
}
